import { useState, useEffect } from 'react';
import type { Project, MarketAnalysis, Persona, Positioning } from '@/utils/pocketbase';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractPersonaName } from '@/utils/markdown';

import { useTranslation } from 'react-i18next';
export interface MarketingStrategyFormData {
  project_id: string;
  market_analysis_id: string;
  persona_id: string;
  positioning_id: string;
  useMarketAnalysis: boolean;
  usePersona: boolean;
  usePositioning: boolean;
  budget: string;
  mainGoal: string;
  teamSize: string;
  currentChannels: string[];
}

interface MarketingStrategyCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: MarketingStrategyFormData) => void;
  projects: Project[];
  marketAnalyses: MarketAnalysis[];
  personas: Persona[];
  positionings: Positioning[];
  isLoading?: boolean;
  initialData?: Partial<MarketingStrategyFormData>;
}

const BUDGET_OPTIONS = [
  { value: 'zero', labelKey: 'options.budget.zero' },
  { value: 'under50k', labelKey: 'options.budget.under50k' },
  { value: '50to300k', labelKey: 'options.budget.50to300k' },
  { value: 'over300k', labelKey: 'options.budget.over300k' },
];

const GOAL_OPTIONS = [
  { value: 'validateIdea', labelKey: 'options.goal.validateIdea' },
  { value: 'firstCustomers', labelKey: 'options.goal.firstCustomers' },
  { value: 'scale', labelKey: 'options.goal.scale' },
  { value: 'retention', labelKey: 'options.goal.retention' },
];

const TEAM_OPTIONS = [
  { value: 'solo', labelKey: 'options.team.solo' },
  { value: 'small', labelKey: 'options.team.small' },
  { value: 'large', labelKey: 'options.team.large' },
];

const CHANNEL_OPTIONS = [
  { value: 'social', labelKey: 'options.channel.social' },
  { value: 'content', labelKey: 'options.channel.content' },
  { value: 'paidAds', labelKey: 'options.channel.paidAds' },
  { value: 'seo', labelKey: 'options.channel.seo' },
  { value: 'partnerships', labelKey: 'options.channel.partnerships' },
  { value: 'email', labelKey: 'options.channel.email' },
  { value: 'none', labelKey: 'options.channel.none' },
];

const GOAL_FRAMEWORKS: Record<string, string[]> = {
  'validateIdea': ['Lean Canvas', 'STP'],
  'firstCustomers': ['Bullseye', 'AARRR'],
  'scale': ['SOSTAC', 'AARRR'],
  'retention': ['SOSTAC', 'STP', 'AARRR'],
};

const initialFormData: MarketingStrategyFormData = {
  project_id: '',
  market_analysis_id: '',
  persona_id: '',
  positioning_id: '',
  useMarketAnalysis: false,
  usePersona: false,
  usePositioning: false,
  budget: '',
  mainGoal: '',
  teamSize: '',
  currentChannels: [],
};

export function MarketingStrategyCreationModal({
  isOpen,
  onClose,
  onSubmit,
  projects,
  marketAnalyses,
  personas,
  positionings,
  isLoading = false,
  initialData,
}: MarketingStrategyCreationModalProps) {
  const { t } = useTranslation('marketing-strategy');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<MarketingStrategyFormData>(initialFormData);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFormData(initialData ? { ...initialFormData, ...initialData } : initialFormData);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Filter data by selected project
  const filteredAnalyses = formData.project_id
    ? marketAnalyses.filter(a => a.project === formData.project_id)
    : [];
  const filteredPersonas = formData.project_id
    ? personas.filter(p => p.project === formData.project_id)
    : [];
  const filteredPositionings = formData.project_id
    ? positionings.filter(p => p.project === formData.project_id)
    : [];

  const selectedProject = projects.find(p => p.id === formData.project_id);

  const handleChannelToggle = (channel: string) => {
    setFormData(prev => {
      const channels = prev.currentChannels.includes(channel)
        ? prev.currentChannels.filter(c => c !== channel)
        : [...prev.currentChannels, channel];
      return { ...prev, currentChannels: channels };
    });
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  // Step validation
  const isStep1Valid = !!formData.project_id;
  const isStep3Valid = !!formData.budget && !!formData.mainGoal && !!formData.teamSize;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-[16px]"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-[24px] p-[24px] md:p-[40px] max-w-[620px] w-full max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <GenerationProgress
            title={t('modal.progressTitle')}
            message={t('modal.progressMessage')}
          />
        ) : (
          <>
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-[8px] mb-[24px]">
              {[1, 2, 3, 4].map(s => (
                <div
                  key={s}
                  className={`
                    w-[32px] h-[32px] rounded-full flex items-center justify-center
                    font-manrope font-bold text-[14px]
                    ${s === step
                      ? 'bg-[#2197f1] text-white'
                      : s < step
                        ? 'bg-[#2197f1]/20 text-[#2197f1]'
                        : 'bg-[#eeeeee] text-[#969696]'
                    }
                  `}
                >
                  {s}
                </div>
              ))}
            </div>

            {/* Step 1: Product Selection */}
            {step === 1 && (
              <>
                <div className="flex flex-col gap-[16px] mb-[32px]">
                  <h2 className="font-manrope font-bold text-[24px] leading-[32px] text-[#0d0d0d]">
                    {t('modal.step1.title')}
                  </h2>
                </div>

                <div className="flex flex-col gap-[24px] mb-[32px]">
                  <div className="flex flex-col gap-[4px]">
                    <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      {t('modal.labelProduct')}
                    </label>
                    <Select
                      value={formData.project_id}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        project_id: value,
                        market_analysis_id: '',
                        persona_id: '',
                        positioning_id: '',
                        useMarketAnalysis: false,
                        usePersona: false,
                        usePositioning: false,
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('modal.placeholderProduct')} />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map(project => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedProject && (
                    <div className="bg-[#f8f8f8] rounded-[16px] p-[16px] flex flex-col gap-[8px]">
                      <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                        <span className="font-semibold">{t('modal.projectInfo.stage')}</span> {selectedProject.stage}
                      </p>
                      {selectedProject.revenue && (
                        <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                          <span className="font-semibold">{t('modal.projectInfo.revenue')}</span> {selectedProject.revenue}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-[24px]">
                  <button
                    onClick={onClose}
                    className="
                      flex-1 bg-transparent
                      border border-[var(--grey-accent,#eeeeee)] border-solid
                      text-[color:var(--black-text,#0d0d0d)]
                      px-[24px] py-[12px] rounded-[16px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center
                      hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors
                    "
                  >
                    {t('modal.buttonClose')}
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    disabled={!isStep1Valid}
                    className={`
                      flex-1 rounded-[16px] px-[24px] py-[12px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center transition-colors
                      ${isStep1Valid
                        ? 'bg-[#2197f1] text-white hover:bg-[#1a7bc7]'
                        : 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-not-allowed opacity-60'
                      }
                    `}
                  >
                    {t('modal.buttonNext')}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Connect Data */}
            {step === 2 && (
              <>
                <div className="flex flex-col gap-[16px] mb-[32px]">
                  <h2 className="font-manrope font-bold text-[24px] leading-[32px] text-[#0d0d0d]">
                    {t('modal.step2.title')}
                  </h2>
                  <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                    {t('modal.step2.subtitle')}
                  </p>
                </div>

                <div className="flex flex-col gap-[20px] mb-[32px]">
                  {/* Market Analysis */}
                  <div className="flex flex-col gap-[8px]">
                    <label className="flex items-center gap-[8px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.useMarketAnalysis}
                        onChange={(e) => setFormData({
                          ...formData,
                          useMarketAnalysis: e.target.checked,
                          market_analysis_id: e.target.checked ? formData.market_analysis_id : '',
                        })}
                        disabled={filteredAnalyses.length === 0}
                        className="w-[18px] h-[18px] rounded-[4px] accent-[#2197f1]"
                      />
                      <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
                        {t('modal.step2.marketAnalysis')}
                      </span>
                    </label>
                    {filteredAnalyses.length === 0 ? (
                      <p className="font-segoe text-[13px] leading-[18px] text-[#969696] ml-[26px]">
                        {t('modal.step2.marketAnalysisHint')}
                      </p>
                    ) : formData.useMarketAnalysis ? (
                      <div className="ml-[26px]">
                        <Select
                          value={formData.market_analysis_id}
                          onValueChange={(value) => setFormData({ ...formData, market_analysis_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('modal.step2.selectAnalysis')} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredAnalyses.map((analysis, index) => (
                              <SelectItem key={analysis.id} value={analysis.id}>
                                {t('modal.step2.analysisItem')}{filteredAnalyses.length > 1 ? ` (${index + 1})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                  </div>

                  {/* Persona */}
                  <div className="flex flex-col gap-[8px]">
                    <label className="flex items-center gap-[8px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.usePersona}
                        onChange={(e) => setFormData({
                          ...formData,
                          usePersona: e.target.checked,
                          persona_id: e.target.checked ? formData.persona_id : '',
                        })}
                        disabled={filteredPersonas.length === 0}
                        className="w-[18px] h-[18px] rounded-[4px] accent-[#2197f1]"
                      />
                      <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
                        {t('modal.step2.persona')}
                      </span>
                    </label>
                    {filteredPersonas.length === 0 ? (
                      <p className="font-segoe text-[13px] leading-[18px] text-[#969696] ml-[26px]">
                        {t('modal.step2.personaHint')}
                      </p>
                    ) : formData.usePersona ? (
                      <div className="ml-[26px]">
                        <Select
                          value={formData.persona_id}
                          onValueChange={(value) => setFormData({ ...formData, persona_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('modal.step2.selectPersona')} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredPersonas.map(persona => (
                              <SelectItem key={persona.id} value={persona.id}>
                                {extractPersonaName(persona)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                  </div>

                  {/* Positioning */}
                  <div className="flex flex-col gap-[8px]">
                    <label className="flex items-center gap-[8px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.usePositioning}
                        onChange={(e) => setFormData({
                          ...formData,
                          usePositioning: e.target.checked,
                          positioning_id: e.target.checked ? formData.positioning_id : '',
                        })}
                        disabled={filteredPositionings.length === 0}
                        className="w-[18px] h-[18px] rounded-[4px] accent-[#2197f1]"
                      />
                      <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
                        {t('modal.step2.positioning')}
                      </span>
                    </label>
                    {filteredPositionings.length === 0 ? (
                      <p className="font-segoe text-[13px] leading-[18px] text-[#969696] ml-[26px]">
                        {t('modal.step2.positioningHint')}
                      </p>
                    ) : formData.usePositioning ? (
                      <div className="ml-[26px]">
                        <Select
                          value={formData.positioning_id}
                          onValueChange={(value) => setFormData({ ...formData, positioning_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('modal.step2.selectPositioning')} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredPositionings.map((pos, index) => (
                              <SelectItem key={pos.id} value={pos.id}>
                                {t('modal.step2.positioningItem')}{filteredPositionings.length > 1 ? ` (${index + 1})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex gap-[24px]">
                  <button
                    onClick={() => setStep(1)}
                    className="
                      flex-1 bg-transparent
                      border border-[var(--grey-accent,#eeeeee)] border-solid
                      text-[color:var(--black-text,#0d0d0d)]
                      px-[24px] py-[12px] rounded-[16px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center
                      hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors
                    "
                  >
                    {t('modal.buttonBack')}
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className="
                      flex-1 rounded-[16px] px-[24px] py-[12px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center transition-colors
                      bg-[#2197f1] text-white hover:bg-[#1a7bc7]
                    "
                  >
                    {t('modal.buttonNext')}
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Strategy Params */}
            {step === 3 && (
              <>
                <div className="flex flex-col gap-[16px] mb-[32px]">
                  <h2 className="font-manrope font-bold text-[24px] leading-[32px] text-[#0d0d0d]">
                    {t('modal.step3.title')}
                  </h2>
                </div>

                <div className="flex flex-col gap-[24px] mb-[32px]">
                  {/* Budget */}
                  <div className="flex flex-col gap-[4px]">
                    <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      {t('modal.step3.labelBudget')}
                    </label>
                    <Select
                      value={formData.budget}
                      onValueChange={(value) => setFormData({ ...formData, budget: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('modal.step3.placeholderBudget')} />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Goal */}
                  <div className="flex flex-col gap-[4px]">
                    <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      {t('modal.step3.labelGoal')}
                    </label>
                    <Select
                      value={formData.mainGoal}
                      onValueChange={(value) => setFormData({ ...formData, mainGoal: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('modal.step3.placeholderGoal')} />
                      </SelectTrigger>
                      <SelectContent>
                        {GOAL_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Team */}
                  <div className="flex flex-col gap-[4px]">
                    <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      {t('modal.step3.labelTeamSize')}
                    </label>
                    <Select
                      value={formData.teamSize}
                      onValueChange={(value) => setFormData({ ...formData, teamSize: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('modal.step3.placeholderTeamSize')} />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Channels */}
                  <div className="flex flex-col gap-[8px]">
                    <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      {t('modal.step3.labelChannels')}
                    </label>
                    <div className="flex flex-col gap-[8px]">
                      {CHANNEL_OPTIONS.map(channel => (
                        <label
                          key={channel.value}
                          className="flex items-center gap-[8px] cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.currentChannels.includes(channel.value)}
                            onChange={() => handleChannelToggle(channel.value)}
                            className="w-[18px] h-[18px] rounded-[4px] accent-[#2197f1]"
                          />
                          <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
                            {t(channel.labelKey)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-[24px]">
                  <button
                    onClick={() => setStep(2)}
                    className="
                      flex-1 bg-transparent
                      border border-[var(--grey-accent,#eeeeee)] border-solid
                      text-[color:var(--black-text,#0d0d0d)]
                      px-[24px] py-[12px] rounded-[16px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center
                      hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors
                    "
                  >
                    {t('modal.buttonBack')}
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    disabled={!isStep3Valid}
                    className={`
                      flex-1 rounded-[16px] px-[24px] py-[12px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center transition-colors
                      ${isStep3Valid
                        ? 'bg-[#2197f1] text-white hover:bg-[#1a7bc7]'
                        : 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-not-allowed opacity-60'
                      }
                    `}
                  >
                    {t('modal.buttonNext')}
                  </button>
                </div>
              </>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <>
                <div className="flex flex-col gap-[16px] mb-[32px]">
                  <h2 className="font-manrope font-bold text-[24px] leading-[32px] text-[#0d0d0d]">
                    {t('modal.step4.title')}
                  </h2>
                </div>

                <div className="flex flex-col gap-[16px] mb-[32px]">
                  {/* Summary */}
                  <div className="bg-[#f8f8f8] rounded-[16px] p-[16px] flex flex-col gap-[8px]">
                    <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      <span className="font-semibold">{t('modal.step4.summaryProduct')}</span> {selectedProject?.name}
                    </p>
                    <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      <span className="font-semibold">{t('modal.step4.summaryBudget')}</span> {t(BUDGET_OPTIONS.find(o => o.value === formData.budget)?.labelKey ?? formData.budget)}
                    </p>
                    <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      <span className="font-semibold">{t('modal.step4.summaryGoal')}</span> {t(GOAL_OPTIONS.find(o => o.value === formData.mainGoal)?.labelKey ?? formData.mainGoal)}
                    </p>
                    <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                      <span className="font-semibold">{t('modal.step4.summaryTeam')}</span> {t(TEAM_OPTIONS.find(o => o.value === formData.teamSize)?.labelKey ?? formData.teamSize)}
                    </p>
                    {formData.currentChannels.length > 0 && (
                      <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                        <span className="font-semibold">{t('modal.step4.summaryChannels')}</span> {formData.currentChannels.map(ch => t(CHANNEL_OPTIONS.find(o => o.value === ch)?.labelKey ?? ch)).join(', ')}
                      </p>
                    )}
                    {formData.useMarketAnalysis && formData.market_analysis_id && (
                      <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                        <span className="font-semibold">{t('modal.step4.summaryAnalysis')}</span> {t('modal.step4.summaryAnalysisConnected')}
                      </p>
                    )}
                    {formData.usePersona && formData.persona_id && (
                      <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                        <span className="font-semibold">{t('modal.step4.summaryPersona')}</span> {t('modal.step4.summaryPersonaConnected')}
                      </p>
                    )}
                    {formData.usePositioning && formData.positioning_id && (
                      <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                        <span className="font-semibold">{t('modal.step4.summaryPositioning')}</span> {t('modal.step4.summaryPositioningConnected')}
                      </p>
                    )}
                  </div>

                  {/* Frameworks */}
                  {formData.mainGoal && GOAL_FRAMEWORKS[formData.mainGoal] && (
                    <div className="flex flex-col gap-[8px]">
                      <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                        {t('modal.step4.frameworksLabel')}
                      </p>
                      <div className="flex flex-wrap gap-[8px]">
                        {GOAL_FRAMEWORKS[formData.mainGoal]!.map(fw => (
                          <span
                            key={fw}
                            className="
                              inline-flex items-center
                              bg-[#2197f1]/10 text-[#2197f1]
                              rounded-[8px] px-[12px] py-[4px]
                              font-segoe font-semibold text-[13px] leading-[20px]
                            "
                          >
                            {fw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-[24px]">
                  <button
                    onClick={() => setStep(3)}
                    className="
                      flex-1 bg-transparent
                      border border-[var(--grey-accent,#eeeeee)] border-solid
                      text-[color:var(--black-text,#0d0d0d)]
                      px-[24px] py-[12px] rounded-[16px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center
                      hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors
                    "
                  >
                    {t('modal.buttonBack')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="
                      flex-1 rounded-[16px] px-[24px] py-[12px]
                      font-segoe font-semibold text-[16px] leading-[24px] text-center transition-colors
                      bg-[#2197f1] text-white hover:bg-[#1a7bc7]
                    "
                  >
                    {t('modal.buttonCreate')}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
